import type { Int, Nullable } from "@/types";

export const isValidString = (value?: unknown): value is string => {
  return typeof value === "string";
};

export const truncate = (text: string, count: number) => {
  return text.length > count ? text.substring(0, count) + "..." : text;
};

export const toSnakeCase = (text: string) => {
  return (
    text &&
    text
      .match(
        /[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g
      )
      ?.map((x) => x.toLowerCase())
      .join("_")
  );
};

export const isBlank = (str: string) => {
  if (typeof str !== "string" || str.trim().length === 0) {
    return true;
  }

  return false;
};

export const isNotBlank = (str: string) => !isBlank(str);

export const quotedStringMapper = (str = "") => `"${str ?? ""}"`;

export const capitalize = (str: string) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1) : str;

export const getAcronym = (text: string, limit?: Int) => {
  if (!text) return "";

  const segments = text.split(/\s|-/);

  const limitedSegments =
    !limit || limit > segments.length ? segments : segments.slice(0, limit);

  return limitedSegments
    .reduce((accumulator, word) => accumulator + word.charAt(0), "")
    .toUpperCase();
};

export const normalizeForSearch = (inputText = "") => {
  if (!inputText) {
    return "";
  }

  // https://stackoverflow.com/a/37511463/704681
  return inputText
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
};

export const toKebabCase = (str: string, handleNumbers = false) => {
  const exp = handleNumbers ? /([a-z]|[0-9])([A-Z]|[0-9])/g : /([a-z])([A-Z])/g;
  return str.replace(exp, "$1-$2").toLowerCase();
};

export const toCamelCase = (str: string) =>
  str.replace(/-./g, (x) => x[1].toUpperCase());

export const toEllipsis = (text: string, length: number) => {
  if (!text) return "";
  return text.length > length ? `${text.substring(0, length)}...` : text;
};

export const sortTexts = (
  textA: string,
  textB: string,
  order: "asc" | "desc" = "asc"
) => {
  return order === "asc"
    ? textA.localeCompare(textB)
    : textB.localeCompare(textA);
};

export const isEqualString = (a?: Nullable<string>, b?: Nullable<string>) => {
  if (!a && !b) return true;
  return a === b;
};

export const parseStringValue = (value: string): boolean | number | string => {
  if (value === "true") return true;
  if (value === "false") return false;

  if (!isNaN(Number(value))) {
    const result = Number(value);
    if (result === 0 && !value.includes("0")) return value;
    return result;
  }

  return value;
};
